/**
 * Pre-built domain trees the Coach walks.
 *
 * Four stock domains: food, medical, retail, office. Each tree is a
 * small state machine:
 *
 *   Stage A — domain detected (Coach acknowledges + asks Q1)
 *   Stage B — answer Q1 (Coach asks Q2)
 *   Stage C — answer Q2 (Coach emits the SparkDraft + "looks right?")
 *
 * The tree is pure data. `engine.ts` is the one that advances state.
 * Keeping the two separated means we can add a new domain by editing
 * this file alone.
 */

import type { DomainType, ParameterDef, PortDefinition } from '@/api/types';

export type CoachDomain =
  | 'food'
  | 'medical'
  | 'retail'
  | 'office'
  // Phase 4.8 — nine new trees covering the missing Ready segments.
  // Order matches the spec brief.
  | 'government'
  | 'legal'
  | 'education'
  | 'cybersecurity'
  | 'industry'
  | 'music'
  | 'video'
  | 'photography'
  | 'defense';

export interface DraftShape {
  name: string;
  description: string;
  version: string;
  domain: DomainType;
  author_name: string;
  inputs: PortDefinition[];
  outputs: PortDefinition[];
  parameters: ParameterDef[];
}

export interface DomainTree {
  id: CoachDomain;
  /** Keyword match against the user's first message, lowercase. */
  keywords: string[];
  /** How the Coach announces which domain it picked. */
  acknowledgement_key: string;
  /** Two clarifying questions. */
  q1_key: string;
  q2_key: string;
  /** How the Coach hands off. */
  ready_key: string;
  /** Produces the final draft. `a1`/`a2` are the user's raw answers. */
  toDraft(a1: string, a2: string): DraftShape;
}

// ─────────────────────────────────────────────────────────────────
// FOOD / bakery
// ─────────────────────────────────────────────────────────────────

const FOOD: DomainTree = {
  id: 'food',
  keywords: [
    'bakery', 'food', 'restaurant', 'kitchen', 'temperature', 'fridge',
    'freezer', 'cold-chain', 'fssc', 'haccp', 'inspector', 'health',
    'cafe', 'catering', 'grocer', 'deli', 'מאפייה', 'מסעדה', 'מטבח',
  ],
  acknowledgement_key: 'trees.food.ack',
  q1_key: 'trees.food.q1',
  q2_key: 'trees.food.q2',
  ready_key: 'trees.food.ready',
  toDraft(a1, a2) {
    const temp_max = parseIntOrDefault(a1, 8);
    const hours = parseIntOrDefault(a2, 4);
    return {
      name: 'temperature-compliance',
      description: `Signs every temperature reading with a timestamp. Flags any reading above ${temp_max}°C. Emits a monthly audit pack the health inspector can verify cryptographically.`,
      version: '1.0.0',
      domain: 'Process',
      author_name: 'you',
      inputs: [
        {
          name: 'temperature_reading',
          data_type: 'Signal',
          required: true,
          schema: { type: 'number' },
        },
        {
          name: 'timestamp',
          data_type: 'Value',
          required: true,
          schema: { type: 'string', format: 'date-time' },
        },
        {
          name: 'inspector_id',
          data_type: 'Value',
          required: false,
          schema: { type: 'string' },
        },
      ],
      outputs: [
        {
          name: 'compliance_report',
          data_type: 'Document',
          required: true,
        },
      ],
      parameters: [
        {
          name: 'max_temp_c',
          description: 'Upper temperature threshold in °C. Readings above trigger an alert.',
          default: { float: temp_max },
          range: { Float: { min: 0, max: 100 } },
        },
        {
          name: 'check_interval_hours',
          description: 'Required interval between consecutive checks.',
          default: { integer: hours },
          range: { Integer: { min: 1, max: 24 } },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// MEDICAL / clinic
// ─────────────────────────────────────────────────────────────────

const MEDICAL: DomainTree = {
  id: 'medical',
  keywords: [
    'clinic', 'patient', 'medical', 'doctor', 'nurse', 'ekg', 'ecg',
    'hospital', 'amar', 'health', 'record', 'dose', 'treatment',
    'fda', 'part-11', 'diagnosis', 'מרפאה', 'רופא', 'מטופל', 'רפואי',
  ],
  acknowledgement_key: 'trees.medical.ack',
  q1_key: 'trees.medical.q1',
  q2_key: 'trees.medical.q2',
  ready_key: 'trees.medical.ready',
  toDraft(a1, a2) {
    const retention_days = parseIntOrDefault(a2, 3650);
    return {
      name: 'patient-record-audit',
      description: `Signs every patient-record access with the accessor's identity and reason. Produces an AMAR-compliant audit log that retains entries for ${retention_days} days. "${truncate(a1, 80)}" is the scope.`,
      version: '1.0.0',
      domain: 'Medical',
      author_name: 'you',
      inputs: [
        {
          name: 'record_id',
          data_type: 'Value',
          required: true,
          schema: { type: 'string' },
        },
        {
          name: 'accessor_id',
          data_type: 'Value',
          required: true,
          schema: { type: 'string' },
        },
        {
          name: 'reason',
          data_type: 'Document',
          required: true,
        },
      ],
      outputs: [
        { name: 'audit_entry', data_type: 'Document', required: true },
      ],
      parameters: [
        {
          name: 'retention_days',
          description: 'How long signed access entries are retained.',
          default: { integer: retention_days },
          range: { Integer: { min: 30, max: 10_950 } },
        },
        {
          name: 'require_reason',
          description: 'Reject accesses that do not supply a reason.',
          default: { bool: true },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// RETAIL / shop
// ─────────────────────────────────────────────────────────────────

const RETAIL: DomainTree = {
  id: 'retail',
  keywords: [
    'shop', 'store', 'retail', 'inventory', 'sale', 'customer',
    'pos', 'transaction', 'amendment-13', 'e-invoice', 'invoice',
    'ecommerce', 'checkout', 'sku', 'חנות', 'קמעונאות', 'מכירה', 'לקוח',
  ],
  acknowledgement_key: 'trees.retail.ack',
  q1_key: 'trees.retail.q1',
  q2_key: 'trees.retail.q2',
  ready_key: 'trees.retail.ready',
  toDraft(a1, _a2) {
    const min_amount = parseIntOrDefault(a1, 0);
    return {
      name: 'transaction-log',
      description: `Signs every customer transaction. Flags transactions at or above $${min_amount} for the regulator. Amendment-13 compliant.`,
      version: '1.0.0',
      domain: 'Financial',
      author_name: 'you',
      inputs: [
        { name: 'transaction_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'amount_ils', data_type: 'Signal', required: true, schema: { type: 'number' } },
        { name: 'customer_ref', data_type: 'Value', required: false, schema: { type: 'string' } },
      ],
      outputs: [
        { name: 'signed_receipt', data_type: 'Document', required: true },
      ],
      parameters: [
        {
          name: 'flag_threshold_ils',
          description: 'Transactions at or above this amount are flagged.',
          default: { integer: min_amount },
          range: { Integer: { min: 0, max: 10_000_000 } },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// OFFICE / accounting
// ─────────────────────────────────────────────────────────────────

const OFFICE: DomainTree = {
  id: 'office',
  keywords: [
    'accountant', 'audit', 'invoice', 'tax', 'bookkeeping', 'expense',
    'mifid', 'payroll', 'ledger', 'receipt', 'office', 'finance',
    'רואה', 'חשבון', 'מס', 'משרד', 'ביקורת', 'קבלה',
  ],
  acknowledgement_key: 'trees.office.ack',
  q1_key: 'trees.office.q1',
  q2_key: 'trees.office.q2',
  ready_key: 'trees.office.ready',
  toDraft(a1, a2) {
    const window_days = parseIntOrDefault(a1, 30);
    const reviewers = parseIntOrDefault(a2, 1);
    return {
      name: 'audit-pack',
      description: `Bundles ${window_days} days of signed transactions into one exportable audit pack. Requires ${reviewers} reviewer signature(s) before release.`,
      version: '1.0.0',
      domain: 'Financial',
      author_name: 'you',
      inputs: [
        { name: 'entries', data_type: 'Stream', required: true },
        { name: 'period_start', data_type: 'Value', required: true, schema: { type: 'string', format: 'date' } },
        { name: 'period_end',   data_type: 'Value', required: true, schema: { type: 'string', format: 'date' } },
      ],
      outputs: [
        { name: 'audit_pack', data_type: 'Document', required: true },
      ],
      parameters: [
        {
          name: 'window_days',
          description: 'Audit-pack period length in days.',
          default: { integer: window_days },
          range: { Integer: { min: 1, max: 365 } },
        },
        {
          name: 'required_reviewers',
          description: 'Number of reviewer signatures required.',
          default: { integer: reviewers },
          range: { Integer: { min: 1, max: 5 } },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// GOVERNMENT / municipality — Phase 4.8 (Citizen Mirror suite).
// q1 picks the workflow class → toDraft branches into the matching
// Mirror recipe. q2 sizes the council so SLA defaults are sensible.
// ─────────────────────────────────────────────────────────────────

const GOVERNMENT: DomainTree = {
  id: 'government',
  keywords: [
    'government', 'municipality', 'municipal', 'council', 'mayor', 'city',
    'citizen', 'procurement', 'tender', 'foia', 'foi ', 'comptroller',
    'permit', 'building permit', 'parking', 'welfare', 'amendment 13',
    'מועצה', 'עירייה', 'ראש עיר', 'מבקר', 'מכרז', 'תושב', 'רשות', 'רישוי',
  ],
  acknowledgement_key: 'trees.government.ack',
  q1_key: 'trees.government.q1',
  q2_key: 'trees.government.q2',
  ready_key: 'trees.government.ready',
  toDraft(a1, a2) {
    const residents = parseIntOrDefault(a2, 50_000);
    const lower = a1.toLowerCase();
    // Branch by topic keyword. Defaults to citizen-request if no match.
    if (lower.includes('council') || lower.includes('vote') || lower.includes('מועצה')) {
      return govDraft('council-decision', residents,
        `Captures roll-call votes on every council motion and amendment, signed by each member at the moment, finalised before adjournment. Sized for ${residents.toLocaleString()} residents.`);
    }
    if (lower.includes('procurement') || lower.includes('tender') || lower.includes('מכרז')) {
      return govDraft('procurement-step', residents,
        `Signs every step of the tender lifecycle — draft, publication, clarifications, evaluation, award, contract — into a chain the comptroller can verify offline. Council size: ${residents.toLocaleString()}.`);
    }
    if (lower.includes('foi') || lower.includes('information') || lower.includes('חופש')) {
      return govDraft('foi-response', residents,
        `Tracks every Freedom-of-Information request against a signed 30-day clock, with redaction grounds and appeal-ready archive. Council size: ${residents.toLocaleString()}.`);
    }
    if (lower.includes('permit') || lower.includes('building') || lower.includes('רישוי')) {
      return govDraft('permit-review', residents,
        `Signs each department's permit review at the moment it ends. Applicant sees the trail; the auditor sees who held the file. Council size: ${residents.toLocaleString()}.`);
    }
    return govDraft('citizen-request', residents,
      `Signs every citizen request the moment it arrives. Auto SLA + 5-step escalation that ends at the State Comptroller. Sized for ${residents.toLocaleString()} residents.`);
  },
};

function govDraft(kind: string, residents: number, description: string): DraftShape {
  return {
    name: `${kind}-mirror`,
    description,
    version: '1.0.0',
    domain: 'Government',
    author_name: 'you',
    inputs: [
      { name: 'event_id', data_type: 'Value', required: true, schema: { type: 'string' } },
      { name: 'officer_id', data_type: 'Value', required: true, schema: { type: 'string' } },
      { name: 'payload', data_type: 'Document', required: true },
    ],
    outputs: [{ name: 'signed_record', data_type: 'Document', required: true }],
    parameters: [
      {
        name: 'residents',
        description: 'Approximate population served — drives default SLA windows.',
        default: { integer: residents },
        range: { Integer: { min: 1_000, max: 1_000_000 } },
      },
      {
        name: 'comptroller_export',
        description: 'Auto-emit a State-Comptroller-schema export bundle on request.',
        default: { bool: true },
      },
    ],
  };
}

// ─────────────────────────────────────────────────────────────────
// LEGAL / chain-of-custody — court-ready FRE 902 evidence packs.
// ─────────────────────────────────────────────────────────────────

const LEGAL: DomainTree = {
  id: 'legal',
  keywords: [
    'legal', 'court', 'evidence', 'custody', 'chain-of-custody', 'fre', 'fre-902',
    'daubert', 'hearing', 'exhibit', 'attorney', 'lawyer', 'litigation',
    'matter', 'deposition', 'investigation', 'forensic',
    'משפט', 'בית-משפט', 'בית משפט', 'ראיה', 'ראיות', 'עורך-דין', 'עורך דין', 'עו"ד',
  ],
  acknowledgement_key: 'trees.legal.ack',
  q1_key: 'trees.legal.q1',
  q2_key: 'trees.legal.q2',
  ready_key: 'trees.legal.ready',
  toDraft(a1, a2) {
    const matters = parseIntOrDefault(a2, 12);
    const lower = a1.toLowerCase();
    const focus =
      lower.includes('custodian') || lower.includes('handoff') ? 'handoff'
      : lower.includes('bundle') || lower.includes('court') ? 'bundle'
      : 'capture';
    return {
      name: 'evidence-chain',
      description:
        focus === 'handoff'
          ? `Dual-signed handoffs between every custodian, with the five W's enforced as required fields. Across ~${matters} active matters per period.`
          : focus === 'bundle'
            ? `Produces FRE 902(13) + 902(14) compliant evidence bundles or a structured refusal — never a quiet "mostly OK". Across ~${matters} matters.`
            : `Captures every piece with a signed oath (Ed25519), SHA-256 of the original, and the five W's. Across ~${matters} active matters.`,
      version: '1.0.0',
      domain: 'Generic',
      author_name: 'you',
      inputs: [
        { name: 'item_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'investigator_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'item_payload', data_type: 'Document', required: true },
        { name: 'legal_authority', data_type: 'Document', required: true },
      ],
      outputs: [
        { name: 'evidence_record', data_type: 'Document', required: true },
        { name: 'fre_902_bundle', data_type: 'Document', required: false },
      ],
      parameters: [
        {
          name: 'active_matters',
          description: 'Concurrent matters this engine serves (per period).',
          default: { integer: matters },
          range: { Integer: { min: 1, max: 5_000 } },
        },
        {
          name: 'require_dual_signature',
          description: 'Refuse a custodian handoff without releasing AND receiving signatures.',
          default: { bool: true },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// EDUCATION / research — preprint timestamp + lab provenance.
// ─────────────────────────────────────────────────────────────────

const EDUCATION: DomainTree = {
  id: 'education',
  keywords: [
    'education', 'university', 'research', 'researcher', 'paper', 'preprint',
    'dataset', 'method', 'figure', 'journal', 'peer-review', 'peer review',
    'academic', 'student', 'lab', 'authorship', 'phd', 'professor',
    'מחקר', 'אוניברסיטה', 'מאמר', 'סטודנט', 'חוקר', 'מעבדה', 'דוקטורט',
  ],
  acknowledgement_key: 'trees.education.ack',
  q1_key: 'trees.education.q1',
  q2_key: 'trees.education.q2',
  ready_key: 'trees.education.ready',
  toDraft(a1, a2) {
    const lower = a1.toLowerCase();
    const subject =
      lower.includes('dataset') || lower.includes('data') ? 'dataset'
      : lower.includes('method') || lower.includes('notebook') ? 'method'
      : 'figure';
    const isLabRecord = a2.toLowerCase().includes('lab') || a2.toLowerCase().includes('permanent');
    return {
      name: `${subject}-shield`,
      description:
        `Stamps your ${subject} with an Ed25519 signature + civil-date timestamp the journal cannot dispute.` +
        (isLabRecord
          ? ' Bound to a permanent lab-record lineage so derived artefacts inherit the chain.'
          : ' Designed for a single paper — fast to mint, easy to cite.'),
      version: '1.0.0',
      domain: 'Generic',
      author_name: 'you',
      inputs: [
        { name: 'artefact', data_type: 'Document', required: true },
        { name: 'sha256', data_type: 'Value', required: true, schema: { type: 'string', pattern: '^[0-9a-f]{64}$' } },
        { name: 'parent_artefact', data_type: 'Value', required: false, schema: { type: 'string' } },
      ],
      outputs: [
        { name: 'priority_certificate', data_type: 'Document', required: true },
      ],
      parameters: [
        {
          name: 'archive_at_zenodo',
          description: 'Cross-anchor the priority certificate at Zenodo for permanent backup.',
          default: { bool: isLabRecord },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// CYBERSECURITY / SOC — incident chain of custody (NIST 800-61, NIS2).
// ─────────────────────────────────────────────────────────────────

const CYBERSECURITY: DomainTree = {
  id: 'cybersecurity',
  keywords: [
    'cyber', 'cybersecurity', 'security', 'soc', 'incident', 'ioc', 'breach',
    'ransomware', 'nist', 'nis2', 'iso 27035', 'siem', 'splunk', 'sentinel',
    'incident-response', 'analyst', 'phishing', 'malware', 'apt',
    'סייבר', 'אבטחה', 'תקרית', 'פריצה', 'נוזקה',
  ],
  acknowledgement_key: 'trees.cybersecurity.ack',
  q1_key: 'trees.cybersecurity.q1',
  q2_key: 'trees.cybersecurity.q2',
  ready_key: 'trees.cybersecurity.ready',
  toDraft(a1, a2) {
    const incidents_per_month = parseIntOrDefault(a2, 30);
    const lower = a1.toLowerCase();
    const regime =
      lower.includes('nis2') || lower.includes('eu') ? 'NIS2 (EU — 24h early warning, 72h notification)'
      : lower.includes('iso') ? 'ISO 27035'
      : lower.includes('incd') || lower.includes('incident management') ? 'INCD'
      : 'NIST 800-61 Rev 2';
    return {
      name: 'incident-chain',
      description: `Signs every IOC, analyst sign-off, and containment action with the analyst-of-record key. Audit posture: ${regime}. Sized for ~${incidents_per_month} incidents/month.`,
      version: '1.0.0',
      domain: 'Generic',
      author_name: 'you',
      inputs: [
        { name: 'incident_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'analyst_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'severity', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'evidence', data_type: 'Document', required: true },
      ],
      outputs: [{ name: 'signed_event', data_type: 'Document', required: true }],
      parameters: [
        {
          name: 'incidents_per_month',
          description: 'Expected throughput — tunes retention + indexing.',
          default: { integer: incidents_per_month },
          range: { Integer: { min: 1, max: 100_000 } },
        },
        {
          name: 'reporting_regime',
          description: 'Which export schema gets pre-populated on demand.',
          default: { string: regime },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// INDUSTRY / supply chain — broader than `food`. Manufacturing,
// pharma, batch traceability, supplier audits.
// ─────────────────────────────────────────────────────────────────

const INDUSTRY: DomainTree = {
  id: 'industry',
  keywords: [
    'industry', 'industrial', 'manufacturing', 'factory', 'pharma', 'batch',
    'supplier', 'supply chain', 'qa', 'quality', 'iso 9001', 'iso-9001',
    'oem', 'plant', 'warehouse', 'logistics', 'shipping',
    'תעשייה', 'ייצור', 'מפעל', 'אצווה', 'ספק', 'איכות',
  ],
  acknowledgement_key: 'trees.industry.ack',
  q1_key: 'trees.industry.q1',
  q2_key: 'trees.industry.q2',
  ready_key: 'trees.industry.ready',
  toDraft(a1, a2) {
    const batches_per_day = parseIntOrDefault(a2, 50);
    const lower = a1.toLowerCase();
    const focus =
      lower.includes('cold') || lower.includes('temperature') ? 'cold-chain'
      : lower.includes('supplier') || lower.includes('audit') ? 'supplier-audit'
      : lower.includes('quality') || lower.includes('qa') || lower.includes('check') ? 'quality-check'
      : 'batch-certificate';
    return {
      name: `${focus}-record`,
      description:
        focus === 'cold-chain'
          ? `Signs every cold-chain reading hourly with sensor identity + timestamp. ~${batches_per_day} batches/day. Hand a regulator a verified chain — not a binder.`
          : focus === 'supplier-audit'
            ? `Captures supplier audits with checklist + inspector identity + corrective-action loop. ~${batches_per_day} batches/day.`
            : focus === 'quality-check'
              ? `Signs every QA pass / fail at the moment it's recorded, against your release criteria. ~${batches_per_day} batches/day.`
              : `Issues a signed certificate per batch with full input lineage. ~${batches_per_day} batches/day.`,
      version: '1.0.0',
      domain: 'Process',
      author_name: 'you',
      inputs: [
        { name: 'batch_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'reading', data_type: 'Signal', required: true },
        { name: 'inspector_id', data_type: 'Value', required: true, schema: { type: 'string' } },
      ],
      outputs: [{ name: 'signed_record', data_type: 'Document', required: true }],
      parameters: [
        {
          name: 'batches_per_day',
          description: 'Expected throughput — tunes capacity defaults.',
          default: { integer: batches_per_day },
          range: { Integer: { min: 1, max: 100_000 } },
        },
        {
          name: 'iso_9001_export',
          description: 'Auto-emit ISO 9001 / FSSC 22000 audit bundle on request.',
          default: { bool: true },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// MUSIC / audio — beats, stems, song registration, sample clearance.
// ─────────────────────────────────────────────────────────────────

const MUSIC: DomainTree = {
  id: 'music',
  keywords: [
    'music', 'song', 'beat', 'beats', 'stem', 'stems', 'daw', 'producer',
    'spotify', 'apple music', 'soundcloud', 'distrokid', 'splice', 'sample',
    'remix', 'mixtape', 'collab', 'royalty',
    'מוזיקה', 'שיר', 'ביט', 'מפיק', 'תמלוגים',
  ],
  acknowledgement_key: 'trees.music.ack',
  q1_key: 'trees.music.q1',
  q2_key: 'trees.music.q2',
  ready_key: 'trees.music.ready',
  toDraft(a1, a2) {
    const lower = a1.toLowerCase();
    const a2Lower = a2.toLowerCase();
    const isCollab = a2Lower.includes('collab') || a2Lower.includes('split') || /\d+\s*\/\s*\d+/.test(a2);
    const kind =
      lower.includes('stem') ? 'stem-pack'
      : lower.includes('song') || lower.includes('register') ? 'song-registration'
      : lower.includes('sample') || lower.includes('clearance') ? 'sample-clearance'
      : lower.includes('collab') || lower.includes('split') ? 'collab-split'
      : 'beat-ownership';
    return {
      name: kind,
      description:
        kind === 'stem-pack'
          ? `Signs each stem (drum, bass, melody, etc.) plus the full pack manifest. Royalty splits${isCollab ? ' co-signed by every collaborator' : ' embedded'}.`
          : kind === 'song-registration'
            ? `Locks the song's hash + title + author at the moment of registration. PRO/MLC export ready.`
            : kind === 'sample-clearance'
              ? `Captures the cleared use: source track, license terms, and signed acknowledgement from rights holder.`
              : kind === 'collab-split'
                ? `Each collaborator signs their stake (e.g. 40/30/30). Disagreement → no proof; agreement → permanent record.`
                : `Beat ownership: hash + producer key + civil-date timestamp. Anyone can verify offline.`,
      version: '1.0.0',
      domain: 'Audio',
      author_name: 'you',
      inputs: [
        { name: 'audio_hash', data_type: 'Value', required: true, schema: { type: 'string', pattern: '^[0-9a-f]{64}$' } },
        { name: 'title', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'collaborators', data_type: 'Document', required: isCollab },
      ],
      outputs: [{ name: 'ownership_certificate', data_type: 'Document', required: true }],
      parameters: [
        {
          name: 'collab_signing',
          description: 'Require every collaborator listed to co-sign before issuing the certificate.',
          default: { bool: isCollab },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// VIDEO / footage chain — release forms, deepfake-proofs, edit chains.
// ─────────────────────────────────────────────────────────────────

const VIDEO: DomainTree = {
  id: 'video',
  keywords: [
    'video', 'film', 'footage', 'shoot', 'camera', 'edit', 'editing', 'cut',
    'davinci', 'premiere', 'frame.io', 'youtube', 'tiktok', 'vimeo',
    'runway', 'kling', 'veo', 'deepfake',
    'וידאו', 'סרט', 'צילום', 'עריכה',
  ],
  acknowledgement_key: 'trees.video.ack',
  q1_key: 'trees.video.q1',
  q2_key: 'trees.video.q2',
  ready_key: 'trees.video.ready',
  toDraft(a1, a2) {
    const hours_per_month = parseIntOrDefault(a2, 20);
    const lower = a1.toLowerCase();
    const kind =
      lower.includes('edit') || lower.includes('chain') ? 'edit-chain'
      : lower.includes('deepfake') || lower.includes('not ai') || lower.includes('real') ? 'deepfake-shield'
      : lower.includes('release') || lower.includes('talent') ? 'release-form'
      : 'footage-proof';
    return {
      name: kind,
      description:
        kind === 'edit-chain'
          ? `Every cut, colour pass, and export signed in order. The full edit history is the proof. ~${hours_per_month} hours/month.`
          : kind === 'deepfake-shield'
            ? `Camera-original SHA-256 signed at the moment of capture. "This is real, not AI-generated" — provable. ~${hours_per_month} hours/month.`
            : kind === 'release-form'
              ? `Talent / location release forms signed by every named party. Bound to the relevant footage hash. ~${hours_per_month} hours/month.`
              : `Footage proof: SHA-256 + camera ID + GPS + civil-date stamp at capture. ~${hours_per_month} hours/month.`,
      version: '1.0.0',
      domain: 'Generic',
      author_name: 'you',
      inputs: [
        { name: 'video_hash', data_type: 'Value', required: true, schema: { type: 'string', pattern: '^[0-9a-f]{64}$' } },
        { name: 'capture_time', data_type: 'Value', required: true, schema: { type: 'string', format: 'date-time' } },
        { name: 'metadata', data_type: 'Document', required: false },
      ],
      outputs: [{ name: 'video_certificate', data_type: 'Document', required: true }],
      parameters: [
        {
          name: 'hours_per_month',
          description: 'Expected footage throughput — tunes storage defaults.',
          default: { integer: hours_per_month },
          range: { Integer: { min: 1, max: 10_000 } },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// PHOTOGRAPHY / portfolio — single shots, albums, prints, licenses.
// ─────────────────────────────────────────────────────────────────

const PHOTOGRAPHY: DomainTree = {
  id: 'photography',
  keywords: [
    'photo', 'photos', 'photography', 'photographer', 'lightroom', 'shutterstock',
    'getty', 'smugmug', '500px', 'exif', 'album', 'portfolio', 'print',
    'license', 'royalty-free', 'editorial',
    'צילום', 'צלם', 'אלבום', 'הדפסה',
  ],
  acknowledgement_key: 'trees.photography.ack',
  q1_key: 'trees.photography.q1',
  q2_key: 'trees.photography.q2',
  ready_key: 'trees.photography.ready',
  toDraft(a1, a2) {
    const lower = a1.toLowerCase();
    const a2Lower = a2.toLowerCase();
    const licensing = a2Lower.includes('yes') || a2Lower.includes('license') || a2Lower.includes('commercial');
    const kind =
      lower.includes('album') || lower.includes('portfolio') ? 'album-registry'
      : lower.includes('print') || lower.includes('edition') ? 'print-certificate'
      : lower.includes('license') ? 'license-manager'
      : 'photo-proof';
    return {
      name: kind,
      description:
        kind === 'album-registry'
          ? 'Bundles 50–500 photos into a signed portfolio. Each image hash + civil-date timestamp; portfolio root is a single verifiable certificate.'
          : kind === 'print-certificate'
            ? 'Issues edition-numbered certificates ("3 of 10") signed by you. Per-print serial + portfolio link bound in.'
            : kind === 'license-manager'
              ? 'Each license signed by both photographer and licensee. Scope, term, and use bound to the cleared image hash.'
              : 'Photo proof: SHA-256 + EXIF capture time + camera ID + photographer key. Portable, verifiable.',
      version: '1.0.0',
      domain: 'Generic',
      author_name: 'you',
      inputs: [
        { name: 'image_hash', data_type: 'Value', required: true, schema: { type: 'string', pattern: '^[0-9a-f]{64}$' } },
        { name: 'capture_time', data_type: 'Value', required: false, schema: { type: 'string', format: 'date-time' } },
        { name: 'license_terms', data_type: 'Document', required: licensing },
      ],
      outputs: [{ name: 'photo_certificate', data_type: 'Document', required: true }],
      parameters: [
        {
          name: 'licensing_enabled',
          description: 'Generate per-license signatures in addition to ownership certificates.',
          default: { bool: licensing },
        },
      ],
    };
  },
};

// ─────────────────────────────────────────────────────────────────
// DEFENSE / official-grade attestation chains.
// ─────────────────────────────────────────────────────────────────

const DEFENSE: DomainTree = {
  id: 'defense',
  keywords: [
    'defense', 'defence', 'military', 'classified', 'restricted', 'official',
    'attestation', 'cleared', 'idf', 'mod', 'pentagon', 'nato', 'export-control',
    'itar', 'ear', 'classified network',
    'ביטחון', 'צבא', 'מסווג', 'אישור', 'יצוא',
  ],
  acknowledgement_key: 'trees.defense.ack',
  q1_key: 'trees.defense.q1',
  q2_key: 'trees.defense.q2',
  ready_key: 'trees.defense.ready',
  toDraft(a1, a2) {
    const lower = a1.toLowerCase();
    const classification =
      lower.includes('classif') || lower.includes('סודי') ? 'CLASSIFIED'
      : lower.includes('restrict') || lower.includes('מוגבל') ? 'RESTRICTED'
      : 'OFFICIAL';
    const multiVendor = a2.toLowerCase().includes('multi') || a2.toLowerCase().includes('vendor');
    return {
      name: 'defense-attestation',
      description: `${classification} attestation chain. Each release point requires the originating cleared signer's identity bound to the artefact hash and the legal authority for release.${multiVendor ? ' Multi-vendor handoffs require dual signature at every transfer.' : ''}`,
      version: '1.0.0',
      domain: 'Defense',
      author_name: 'you',
      inputs: [
        { name: 'artefact_hash', data_type: 'Value', required: true, schema: { type: 'string', pattern: '^[0-9a-f]{64}$' } },
        { name: 'cleared_signer_id', data_type: 'Value', required: true, schema: { type: 'string' } },
        { name: 'legal_authority', data_type: 'Document', required: true },
      ],
      outputs: [{ name: 'attestation', data_type: 'Document', required: true }],
      parameters: [
        {
          name: 'classification',
          description: 'Highest classification this chain handles.',
          default: { string: classification },
        },
        {
          name: 'multi_vendor',
          description: 'Require dual signature on inter-vendor handoffs.',
          default: { bool: multiVendor },
        },
      ],
    };
  },
};

export const TREES: Record<CoachDomain, DomainTree> = {
  food: FOOD,
  medical: MEDICAL,
  retail: RETAIL,
  office: OFFICE,
  government: GOVERNMENT,
  legal: LEGAL,
  education: EDUCATION,
  cybersecurity: CYBERSECURITY,
  industry: INDUSTRY,
  music: MUSIC,
  video: VIDEO,
  photography: PHOTOGRAPHY,
  defense: DEFENSE,
};

// ─── Helpers ──────────────────────────────────────────────────────

function parseIntOrDefault(s: string, fallback: number): number {
  const m = s.match(/-?\d+/);
  if (!m) return fallback;
  const n = parseInt(m[0]!, 10);
  return Number.isFinite(n) ? n : fallback;
}

function truncate(s: string, max: number): string {
  const clean = s.replace(/\s+/g, ' ').trim();
  return clean.length <= max ? clean : clean.slice(0, max - 1).trimEnd() + '…';
}
